import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import classnames from 'classnames'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

import {
  AiSuggestionReview,
  IsPurchaseDateValidForEmployeeResponse,
  SaveAiDecisionRequest,
  isPurchaseDateValidForEmployee,
  saveAiDecision,
} from 'core'
import getExpenseDetailsForReview, {
  ExpenseForReview,
} from 'core/application/docReviewDashboard/expenses/expenseDetails'
import reviewExpense, { ReviewExpenseRequest } from 'core/application/docReviewDashboard/expenses/reviewExpense'
import { ReviewerAction } from 'core/application/docReviewDashboard/expenses/reviewerAction'
import skipExpenseForReview from 'core/application/docReviewDashboard/expenses/skipExpenseForReview'

import {
  Button,
  CustomCard as Card,
  Col,
  DatePicker,
  DefaultRadioGroupV2,
  DefaultSelect,
  FormikSelectWithChildrenField,
  FormikTextAreaField,
  HtmlEditor,
  Icon,
  InputNumber,
  InputV2,
  Option,
  Row,
  Skeleton,
  Tooltip,
  filterByTooltipValue,
  filterByValue,
} from 'common/components'
import { parseZoneFormatV2, toDateWithoutTimeZoneV2 } from 'common/dateFormatters'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

import { ActionIcon } from '../../common/ActionIcon'
import DocumentComponent from '../../common/DocumentComponet'
import UserInfo from '../../common/userInfo'
import PriorExpenses from './PriorExpenses'

import './styles.less'

type ReviewExpenseProps = {
  expenseId: string
  refreshAfterReview?: boolean
  onExpenseReviewed?: (expenseId: string) => void
  onExpenseSkipped?: (expenseId: string) => void
  onPriorExpenseClick?: (expenseId: string) => void
}

const ReviewExpenseForm = (props: ReviewExpenseProps) => {
  const { t: translate } = useTranslation()

  const {
    data: expense,
    isLoading: isExpenseLoading,
    sendQuery: sendGetExpense,
  } = useQueryRequestV2<ExpenseForReview>({ initialState: null })
  const [showViewHistory, setShowViewHistory] = useState(false)
  const [initialValues, setInitialValues] = useState<FormData | null>()
  const { sendCommand: sendReviewExpenseCommand, isLoading: isReviewExpenseCommandLoading } = useCommandRequestV2(
    (request: ReviewExpenseRequest) => reviewExpense(request),
  )
  const { sendCommand: sendSkipExpenseFoReviewCommand, isLoading: isSkipExpenseFoReviewCommandLoading } =
    useCommandRequestV2((expenseId: string) => skipExpenseForReview(expenseId))

  const { sendCommand: sendSaveAiDecision } = useCommandRequestV2((saveAiDecisionRequest: SaveAiDecisionRequest) =>
    saveAiDecision(saveAiDecisionRequest),
  )

  const { sendQuery: sendIsPurchaseDateValidForEmployee } = useQueryRequestV2<IsPurchaseDateValidForEmployeeResponse>({
    initialState: null,
  })
  const [overrideServiceDateValidResponse, setOverrideServiceDateValidResponse] =
    useState<IsPurchaseDateValidForEmployeeResponse>(IsPurchaseDateValidForEmployeeResponse.Correct)

  useEffect(() => {
    ;(async () => {
      if (props.expenseId) {
        await sendGetExpense(() => getExpenseDetailsForReview(props.expenseId))
      }
    })()
  }, [props.expenseId])

  useEffect(() => {
    if (expense) {
      setInitialValues({
        ...expense,
        overrideServiceDateSelect: expense.lastReview
          ? expense.lastReview.overrideServiceDate
            ? parseZoneFormatV2(expense.lastReview.overrideServiceDate)
            : expense.lastReview.purchasedDateReviewStatus
          : undefined,
        overrideServiceDate: expense.lastReview?.overrideServiceDate,
        overrideAmountSelect: expense.lastReview
          ? expense.lastReview.overrideAmount
            ? expense.lastReview.overrideAmount
            : expense.lastReview.amountReviewStatus
          : undefined,
        overrideAmount: expense.lastReview?.overrideAmount,
        overrideExpenseTypeIdSelect: expense.lastReview
          ? expense.lastReview.overrideExpenseTypeId
            ? expense.availableExpenseTypes.find((x) => x.id === expense.lastReview.overrideExpenseTypeId)?.name
            : expense.lastReview.expenseTypeReviewStatus
          : undefined,
        overrideExpenseTypeId: expense.lastReview?.overrideExpenseTypeId,
        overrideServiceProviderSelect: expense.lastReview
          ? expense.lastReview.overrideServiceProvider
            ? expense.lastReview.overrideServiceProvider
            : expense.lastReview.serviceProviderReviewStatus
          : ReviewerAction.Verified,
        overrideServiceProvider: expense.lastReview?.overrideServiceProvider,
        overrideFamilyMemberIdSelect: expense.lastReview
          ? expense.lastReview.overrideExpenseFor
            ? expense.availableFamilyMembers.find((x) => x.id === expense.lastReview.overrideExpenseFor.id)?.name
            : expense.lastReview.familyMemberReviewStatus
          : ReviewerAction.Verified,
        overrideFamilyMemberId: expense.lastReview?.overrideExpenseFor?.id,
        messageToReviewer: expense.lastReview?.messageToReviewer,
        reviewSectionEOBStatusSelect:
          expense.lastReview && expense.lastReview.reviewSectionEOBStatus != null
            ? expense.lastReview.reviewSectionEOBStatus
            : undefined,
        aiSuggestionReview: expense.aiSuggestionReview,
      })
      setOverrideServiceDateValidResponse(IsPurchaseDateValidForEmployeeResponse.Correct)
    }
  }, [expense])

  const saveReview = async (formik: FormikProps<FormData>) => {
    const request: ReviewExpenseRequest = {
      expenseId: expense.expenseId,
      overrideExpenseTypeId:
        formik.values.overrideExpenseTypeId !== -1 && formik.values.overrideExpenseTypeId !== -2
          ? formik.values.overrideExpenseTypeId
          : null,
      expenseTypeReviewStatus: getReviewActionStatus(formik.values.overrideExpenseTypeIdSelect),
      overrideAmount: formik.values.overrideAmount,
      amountReviewStatus: getReviewActionStatus(formik.values.overrideAmountSelect),
      overrideServiceDate: formik.values.overrideServiceDate,
      overrideServiceProvider: formik.values.overrideServiceProvider,
      overrideFamilyMemberId: formik.values.overrideFamilyMemberId,
      serviceProviderReviewStatus: getReviewActionStatus(formik.values.overrideServiceProviderSelect),
      familyMemberIdReviewStatus: getReviewActionStatus(formik.values.overrideFamilyMemberIdSelect),
      purchasedDateReviewStatus: getReviewActionStatus(formik.values.overrideServiceDateSelect),
      messageToEmployee: formik.values.messageToEmployee,
      messageToReviewer: formik.values.messageToReviewer,
      ineligibleExpenseType: formik.values.overrideExpenseTypeId === -1,
      isDuplicate: formik.values.overrideExpenseTypeId === -2,
      reviewSectionEOBStatus: getReviewActionStatus(formik.values.reviewSectionEOBStatusSelect),
      aiSuggestionReview: formik.values.aiSuggestionReview,
    }

    const result = await sendReviewExpenseCommand(request)

    if (result.completed) {
      if (props.refreshAfterReview) {
        await sendGetExpense(() => getExpenseDetailsForReview(expense.expenseId))
      }

      window.scrollTo(0, 0)

      if (props.onExpenseReviewed) {
        props.onExpenseReviewed(expense.expenseId)
      }
    }
  }

  const handleKeyPress = (e) => {
    const keyCode = e.keyCode || e.which
    const keyValue = String.fromCharCode(keyCode)

    if (!/^(\d)*(\.)?([0-9]{1})?$/.test(keyValue)) e.preventDefault()
  }

  const skipExpense = async (formik: FormikProps<FormData>) => {
    const result = await sendSkipExpenseFoReviewCommand(expense.expenseId)

    if (result.completed) {
      window.scrollTo(0, 0)
      formik.resetForm()

      if (props.onExpenseSkipped) {
        props.onExpenseSkipped(expense.expenseId)
      }
    }
  }

  const getMessageToEmployeeValue = (formik: FormikProps<FormData>) => {
    if (formik.values.messageToEmployeeTemplateSelect) {
      const messages = expense.availableCannedMessages.filter(
        (c) => c.id === formik.values.messageToEmployeeTemplateSelect,
      )
      return messages.length > 0 ? messages[0].message : ''
    }

    return ''
  }

  const saveAiDecisionHandler = async (event) => {
    const value = event.target.value
    await sendSaveAiDecision({
      expenseId: expense.expenseId,
      aiSuggestionReview: value,
    })
  }

  return (
    <Skeleton loading={isExpenseLoading}>
      {initialValues && (
        <Card
          title={`${translate('doc-review-dashboard.expenses.review-expense-title')} #${initialValues.expenseNumber}`}
        >
          <Row>
            <Col span={24} className="mt-4 mb-4">
              <UserInfo
                name={`${initialValues.companyUser.firstName} ${initialValues.companyUser.lastName}`}
                {...initialValues.companyUser}
              />
            </Col>
          </Row>

          <div className="flex__space_between mx-16 my-8 gap-16">
            <div>
              <span className="color-gray-6">{translate('doc-review-dashboard.expenses.plan-eligibility')}</span>
              {initialValues.planEligibilities.map((item: any, index: number) => {
                return (
                  <div key={index} className={item.isInPlanYear ? 'review_expense__benefit_info_font_bold' : ''}>
                    {parseZoneFormatV2(item.startDate)} - {parseZoneFormatV2(item.endDate)}
                  </div>
                )
              })}
            </div>
            <div>
              <span className="color-gray-6">{translate('doc-review-dashboard.expenses.status')}</span>
              <p>{initialValues.status.name}</p>
            </div>
            <div>
              <span className="color-gray-6">{translate('doc-review-dashboard.expenses.review-history')}</span>
              <p>{initialValues.reviewHistory ? initialValues.reviewHistory : 'new'}</p>
              {showViewHistory && (
                <p className="color-blue-6 cursor_pointer" onClick={() => {}}>
                  {translate('doc-review-dashboard.expenses.view-history-details')}
                </p>
              )}
              <div>
                <div>
                  <b>Original Submission Date:</b> {parseZoneFormatV2(initialValues.firstSubmittedOn)}
                </div>

                {initialValues.lastSubmittedAt && (
                  <div>
                    <b>Awaiting review:</b> {parseZoneFormatV2(initialValues.lastSubmittedAt)}
                  </div>
                )}
              </div>
            </div>

            {initialValues?.restrictedExpenseCategories && initialValues?.restrictedExpenseCategories.length > 0 && (
              <div>
                <span className="color-gray-6"> Restricted Expense Categories</span>
                <p>{initialValues?.restrictedExpenseCategories?.map((c) => c.name)?.join('/')}</p>
              </div>
            )}
            <div>
              <span className="color-gray-6">{translate('doc-review-dashboard.expenses.product')}</span>
              <p>{initialValues.productName}</p>
            </div>
          </div>

          <Row>
            <Col span={17}>
              <Formik
                enableReinitialize={true}
                validationSchema={formValidator(translate)}
                validateOnMount={true}
                initialValues={initialValues}
                onSubmit={() => {}}
              >
                {(formik: FormikProps<FormData>) => (
                  <form onSubmit={formik.handleSubmit} autoComplete="off">
                    <Row className="review_form_header">
                      <Col span={8}>
                        <div></div>
                      </Col>
                      <Col span={8}>
                        <div>
                          <h6>{translate('doc-review-dashboard.expenses.review-expense-form.participant')}</h6>
                        </div>
                      </Col>
                      <Col span={8}>
                        <div>
                          <h6>{translate('doc-review-dashboard.expenses.review-expense-form.reviewer')}</h6>
                        </div>
                      </Col>
                    </Row>

                    <ExpenseFormItem
                      itemName={translate('doc-review-dashboard.expenses.review-expense-form.date-submitted')}
                      itemValue={parseZoneFormatV2(initialValues.lastSubmittedAt ?? initialValues.firstSubmittedOn)}
                    />

                    <ExpenseFormItem
                      itemName={translate('doc-review-dashboard.expenses.review-expense-form.expense-id')}
                      itemValue={initialValues.expenseNumber}
                    />

                    <ExpenseFormItemSelect
                      itemDisplayName={'Date of service'}
                      itemDisplayValue={parseZoneFormatV2(initialValues.purchasedDate)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          if (formik.values?.overrideServiceDate) {
                            formik.setFieldValue('overrideServiceDateSelect', formik.values?.overrideServiceDate)
                          }
                        }
                      }}
                      inputName={'overrideServiceDateSelect'}
                      inputValue={
                        formik.values?.overrideServiceDateSelect != undefined
                          ? [formik.values?.overrideServiceDateSelect]
                          : undefined
                      }
                      disabled={!initialValues.canReview}
                      actionComponentError={
                        overrideServiceDateValidResponse !== IsPurchaseDateValidForEmployeeResponse.Correct
                          ? `Value is outside user's eligibility dates.`
                          : null
                      }
                      customOption={
                        <DatePicker
                          placeholder="MM/DD/YYYY"
                          name="overrideServiceDate"
                          defaultValue={
                            formik.values?.overrideServiceDate
                              ? toDateWithoutTimeZoneV2(formik.values.overrideServiceDate)
                              : undefined
                          }
                          onChange={async (value: any) => {
                            if (value) {
                              formik.setFieldValue('overrideServiceDateSelect', parseZoneFormatV2(value))
                              const result = await sendIsPurchaseDateValidForEmployee(() =>
                                isPurchaseDateValidForEmployee(
                                  initialValues.planPeriodId,
                                  initialValues.companyUser.id,
                                  value,
                                ),
                              )
                              setOverrideServiceDateValidResponse(result)
                            } else {
                              formik.setFieldValue('overrideServiceDateSelect', ReviewerAction.Verified)
                            }
                          }}
                          id="overrideServiceDate"
                          format="MM/DD/YYYY"
                          noStyles
                        />
                      }
                      actionIcon={getActionIcon(formik.values?.overrideServiceDateSelect)}
                    />

                    <ExpenseFormItemSelect
                      itemDisplayName={translate('doc-review-dashboard.expenses.review-expense-form.amount-submitted')}
                      itemDisplayValue={`$${initialValues.requestedAmount}`}
                      inputName={'overrideAmountSelect'}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          if (formik.values?.overrideAmount) {
                            formik.setFieldValue('overrideAmountSelect', formik.values?.overrideAmount)
                          }
                        }
                      }}
                      inputValue={
                        formik.values?.overrideAmountSelect != undefined
                          ? [formik.values?.overrideAmountSelect]
                          : undefined
                      }
                      disabled={!initialValues.canReview}
                      customOption={
                        <InputNumber
                          name="overrideAmount"
                          min={0.01}
                          onChange={(value) => {
                            formik.setFieldValue('overrideAmountSelect', value ?? ReviewerAction.Verified)
                          }}
                          formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          onKeyPress={handleKeyPress}
                          parser={(value) => value!.replace(/\$\s?|(,*)/g, '')}
                        />
                      }
                      actionIcon={getActionIcon(formik.values?.overrideAmountSelect)}
                    />

                    <ExpenseFormItemSelect
                      itemDisplayName={translate('doc-review-dashboard.expenses.review-expense-form.expense-type')}
                      itemDisplayValue={initialValues.expenseType.name}
                      inputName={'overrideExpenseTypeIdSelect'}
                      inputValue={
                        formik.values?.overrideExpenseTypeIdSelect != undefined
                          ? [formik.values?.overrideExpenseTypeIdSelect]
                          : undefined
                      }
                      disabled={!initialValues.canReview}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          if (formik.values?.overrideExpenseTypeId) {
                            const found = initialValues.availableExpenseTypes.find(
                              (x) => x.id == formik.values?.overrideExpenseTypeId,
                            )
                            formik.setFieldValue('overrideExpenseTypeIdSelect', found?.name)
                          }
                        }
                      }}
                      customOption={
                        <DefaultSelect
                          showSearch
                          name="overrideExpenseTypeId"
                          placeholder="Search"
                          filterOption={filterByTooltipValue}
                          onChange={(value) => {
                            const found = initialValues.availableExpenseTypes.find((x) => x.id == value)
                            formik.setFieldValue('overrideExpenseTypeIdSelect', found?.name)
                          }}
                        >
                          {initialValues.availableExpenseTypes.map((ae) => {
                            return (
                              <Option key={ae.id} value={ae.id}>
                                <Tooltip title={ae.name}>
                                  <span>{ae.name}</span>
                                </Tooltip>
                              </Option>
                            )
                          })}
                        </DefaultSelect>
                      }
                      customOptionClassname={'w-100'}
                      actionIcon={getActionIcon(formik?.values.overrideExpenseTypeIdSelect)}
                    />

                    <ExpenseFormItemSelect
                      itemDisplayName={translate(
                        'doc-review-dashboard.expenses.review-expense-form.who-the-expense-was-for',
                      )}
                      itemDisplayValue={initialValues.expenseFor.name}
                      inputName={'overrideFamilyMemberIdSelect'}
                      inputValue={
                        formik.values?.overrideFamilyMemberIdSelect != undefined
                          ? [formik.values?.overrideFamilyMemberIdSelect]
                          : undefined
                      }
                      disabled={!initialValues.canReview}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          if (formik.values?.overrideFamilyMemberId) {
                            const found = initialValues.availableFamilyMembers.find(
                              (x) => x.id == formik.values?.overrideFamilyMemberId,
                            )
                            formik.setFieldValue('overrideFamilyMemberIdSelect', found?.name)
                          }
                        }
                      }}
                      customOption={
                        <DefaultSelect
                          showSearch
                          name="overrideFamilyMemberId"
                          placeholder="Search"
                          filterOption={filterByTooltipValue}
                          onChange={(value) => {
                            const found = initialValues.availableFamilyMembers.find((x) => x.id === value)
                            formik.setFieldValue('overrideFamilyMemberIdSelect', found?.name)
                          }}
                        >
                          {initialValues.availableFamilyMembers.map((ae) => {
                            return (
                              <Option key={ae.id} value={ae.id}>
                                <Tooltip title={ae.name}>
                                  <span>{ae.name}</span>
                                </Tooltip>
                              </Option>
                            )
                          })}
                        </DefaultSelect>
                      }
                      customOptionClassname={'w-100'}
                      actionIcon={getActionIcon(formik?.values.overrideFamilyMemberIdSelect)}
                    />

                    <ExpenseFormItemSelect
                      itemDisplayName={translate('doc-review-dashboard.expenses.review-expense-form.service-provider')}
                      itemDisplayValue={initialValues.serviceProvider}
                      inputName={'overrideServiceProviderSelect'}
                      inputValue={
                        formik.values?.overrideServiceProviderSelect != undefined
                          ? [formik.values?.overrideServiceProviderSelect]
                          : undefined
                      }
                      disabled={!initialValues.canReview}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault()
                          if (!!formik.values?.overrideServiceProvider) {
                            formik.setFieldValue(
                              'overrideServiceProviderSelect',
                              formik.values?.overrideServiceProvider,
                            )
                          }
                        }
                      }}
                      customOption={
                        <InputV2
                          name="overrideServiceProvider"
                          onChange={(value) => {
                            formik.setFieldValue(
                              'overrideServiceProviderSelect',
                              !!value ? value : ReviewerAction.Verified,
                            )
                          }}
                        />
                      }
                      actionIcon={getActionIcon(formik.values?.overrideServiceProviderSelect)}
                    />

                    {formik.values.requireEobVerification && (
                      <ExpenseFormItemSelect
                        itemDisplayName={translate('doc-review-dashboard.expenses.review-expense-form.eob-required')}
                        itemDisplayValue={''}
                        inputValue={
                          formik.values?.reviewSectionEOBStatusSelect != undefined
                            ? [formik.values?.reviewSectionEOBStatusSelect]
                            : undefined
                        }
                        inputName={'reviewSectionEOBStatusSelect'}
                        disabled={!initialValues.canReview}
                        actionIcon={getActionIcon(formik?.values.reviewSectionEOBStatusSelect)}
                      />
                    )}
                    <ExpenseFormItem
                      itemName={`AI Suggestion: `}
                      itemValue={<b>{initialValues.aiSuggestion.name}</b>}
                      actionComponent={
                        <DefaultRadioGroupV2
                          name={'aiSuggestionReview'}
                          onChange={saveAiDecisionHandler}
                          className="-mb-12"
                          direction="vertical"
                          items={[
                            { name: 'Agree', value: AiSuggestionReview.Positive },
                            { name: 'AWC', value: AiSuggestionReview.AgreeWithConditions },
                            { name: 'Disagree', value: AiSuggestionReview.Negative },
                            { name: 'No Decision', value: AiSuggestionReview.NoDecision },
                          ]}
                        />
                      }
                    ></ExpenseFormItem>

                    <ExpenseFormItem
                      itemName={translate('doc-review-dashboard.expenses.review-expense-form.documents-submitted')}
                      itemValue={
                        <DocumentComponent
                          expenseTypeEligibility={initialValues.expenseTypeEligibility}
                          documents={initialValues.attachments}
                        />
                      }
                      itemValueSpan={16}
                    />

                    <ExpenseFormItem
                      itemName={translate('doc-review-dashboard.expenses.review-expense-form.notes-from-user')}
                      itemValue={initialValues.description}
                    />

                    <ExpenseFormItem
                      itemName={translate('doc-review-dashboard.expenses.review-expense-form.message-to-employee')}
                      actionComponent={
                        <div>
                          <Row justify="end" gutter={[16, 16]}>
                            <Col span={15}>
                              <DefaultSelect
                                showSearch
                                name="messageToEmployeeTemplateSelect"
                                placeholder="Add template"
                                filterOption={filterByValue}
                                disabled={!initialValues.canReview}
                                className="w-100 mb-8"
                              >
                                {initialValues.availableCannedMessages.map((cannedMessage) => {
                                  return (
                                    <Option key={cannedMessage.id} value={cannedMessage.id}>
                                      {cannedMessage.title}
                                    </Option>
                                  )
                                })}
                              </DefaultSelect>
                            </Col>
                          </Row>

                          <HtmlEditor name="messageToEmployee" value={getMessageToEmployeeValue(formik)} />
                        </div>
                      }
                    />

                    <ExpenseFormItem
                      itemName={translate('doc-review-dashboard.expenses.review-expense-form.internal-notes')}
                      actionComponent={
                        <FormikTextAreaField name="messageToReviewer" disabled={!initialValues.canReview} />
                      }
                    />

                    <Row>
                      <Col>
                        <div className="m16-24">
                          <Button
                            onClick={() => saveReview(formik)}
                            type="primary"
                            disabled={
                              !formValidator(translate).isValidSync(formik.values) ||
                              isReviewExpenseCommandLoading ||
                              !initialValues.canReview ||
                              isExpenseLoading
                            }
                            className=" mr-8"
                            loading={isReviewExpenseCommandLoading || isExpenseLoading}
                          >
                            {translate('general.save')}
                          </Button>

                          <Button
                            onClick={() => skipExpense(formik)}
                            type="default"
                            disabled={isSkipExpenseFoReviewCommandLoading || !initialValues.canReview}
                            loading={isSkipExpenseFoReviewCommandLoading}
                          >
                            {translate('general.skip')}
                          </Button>
                        </div>
                      </Col>
                    </Row>
                  </form>
                )}
              </Formik>
            </Col>

            <Col span={7}>
              <PriorExpenses expenseId={props.expenseId} onPriorExpenseClick={props.onPriorExpenseClick} />
            </Col>
          </Row>
        </Card>
      )}
    </Skeleton>
  )
}

export default ReviewExpenseForm

type FormData = ExpenseForReview & {
  overrideServiceDateSelect?: string | number
  overrideServiceDate?: string
  overrideAmountSelect?: string | number
  overrideAmount?: number
  overrideExpenseTypeIdSelect?: string | number
  overrideExpenseTypeId?: number
  messageToReviewer?: string
  messageToEmployee?: string
  messageToEmployeeTemplateSelect?: string
  reviewSectionEOBStatusSelect?: string | number
  overrideFamilyMemberIdSelect?: string | number
  overrideFamilyMemberId?: string
  overrideServiceProviderSelect?: string | number
  overrideServiceProvider?: string
}

const formValidator = (translate: any) =>
  Yup.object({
    overrideServiceDateSelect: Yup.mixed()
      .nullable()
      .when('overrideServiceDate', {
        is: (overrideServiceDate: string) => overrideServiceDate === undefined,
        then: Yup.number()
          .required(translate('general.required'))
          .oneOf([ReviewerAction.Missing, ReviewerAction.Verified]),
      }),

    overrideAmountSelect: Yup.mixed()
      .nullable()
      .when('overrideAmount', {
        is: (overrideAmount: number) => overrideAmount === undefined,
        then: Yup.number()
          .required(translate('general.required'))
          .oneOf([ReviewerAction.Missing, ReviewerAction.Verified]),
      }),
    overrideAmount: Yup.number().nullable().min(0.01).notRequired(),

    overrideServiceProviderSelect: Yup.mixed()
      .nullable()
      .when('overrideServiceProvider', {
        is: (overrideServiceProvider: number) => overrideServiceProvider === undefined,
        then: Yup.number()
          .required(translate('general.required'))
          .oneOf([ReviewerAction.Missing, ReviewerAction.Verified]),
      }),

    overrideExpenseTypeIdSelect: Yup.mixed()
      .nullable()
      .when('overrideExpenseTypeId', {
        is: (overrideExpenseTypeId: number) => overrideExpenseTypeId == undefined,
        then: Yup.number()
          .required(translate('general.required'))
          .oneOf([ReviewerAction.Missing, ReviewerAction.Verified]),
      }),

    overrideFamilyMemberIdSelect: Yup.mixed()
      .nullable()
      .when('overrideFamilyMemberId', {
        is: (overrideFamilyMemberId: number) => overrideFamilyMemberId == undefined,
        then: Yup.number()
          .required(translate('general.required'))
          .oneOf([ReviewerAction.Missing, ReviewerAction.Verified]),
      }),
    reviewSectionEOBStatusSelect: Yup.mixed()
      .nullable()
      .when('requireEobVerification', {
        is: (requireEobVerification: boolean) => requireEobVerification === true,
        then: Yup.number()
          .required(translate('general.required'))
          .oneOf([ReviewerAction.Missing, ReviewerAction.Verified]),
      }),

    messageToEmployee: Yup.string()
      .nullable()
      .when(
        [
          'overrideServiceDateSelect',
          'overrideAmountSelect',
          'overrideExpenseTypeIdSelect',
          'reviewSectionEOBStatusSelect',
          'overrideFamilyMemberIdSelect',
          'overrideServiceProviderSelect',
        ],
        {
          is: (
            overrideServiceDateSelect,
            overrideAmountSelect,
            overrideExpenseTypeIdSelect,
            reviewSectionEOBStatusSelect,
            overrideFamilyMemberIdSelect,
            overrideServiceProviderSelect,
          ) => {
            return (
              overrideServiceDateSelect === ReviewerAction.Missing ||
              overrideAmountSelect === ReviewerAction.Missing ||
              overrideExpenseTypeIdSelect === ReviewerAction.Missing ||
              reviewSectionEOBStatusSelect === ReviewerAction.Missing ||
              overrideFamilyMemberIdSelect === ReviewerAction.Missing ||
              overrideServiceProviderSelect === ReviewerAction.Missing
            )
          },
          then: Yup.string().required(translate('general.required')),
        },
      ),
  })

const getReviewActionStatus = (value: any): ReviewerAction => {
  if (value === ReviewerAction.Verified) return ReviewerAction.Verified

  if (value === ReviewerAction.Missing) return ReviewerAction.Missing

  return ReviewerAction.Override
}

const getActionIcon = (value?: any): ActionIcon | null => {
  if (value === undefined || value === null || value === '') return null

  if (value === ReviewerAction.Verified) return ActionIcon.VerifiedActionIcon

  if (value === ReviewerAction.Missing) return ActionIcon.MissingActionIcon

  return ActionIcon.OverrideActionIcon
}

type ExpenseFormItemProps = {
  itemName: string
  itemValue?: any
  itemValueSpan?: number
  showEmptyItemValue?: boolean
  actionComponent?: ReactNode
  actionComponentError?: string
  actionIcon?: ActionIcon | null
}

const ExpenseFormItem: React.FC<ExpenseFormItemProps> = ({
  itemName,
  itemValue,
  itemValueSpan,
  actionComponent,
  actionComponentError,
  actionIcon,
}) => {
  const iconClassName = classnames({
    'color-green-6': actionIcon === ActionIcon.VerifiedActionIcon,
    'far fa-times-circle color-red-6': actionIcon === ActionIcon.MissingActionIcon,
    'color-yellow-6': actionIcon === ActionIcon.OverrideActionIcon,
    'ml-8': true,
  })

  const showFullRow = itemName === 'Message to employee' || itemName === 'Internal notes'

  return (
    <Row className="review_form_item flex flex__center m-0" gutter={[16, 16]}>
      <Col span={8}>
        <div>
          <span>{itemName}</span>
        </div>
      </Col>
      {itemValue && (
        <Col span={actionComponent ? 6 : 16}>
          <div>
            <span>{itemValue}</span>
          </div>
        </Col>
      )}
      {actionComponent && (
        <Col span={itemValue || itemValue === '' ? 10 : 16}>
          <Row align="middle">
            <Col span={(showFullRow && 24) || 20}>{actionComponent}</Col>
            {actionIcon && (
              <Col span={4} style={{ height: '100%' }}>
                <div>
                  <Icon type={`far fa-${actionIcon}`} fontAwesome className={iconClassName} />
                </div>
              </Col>
            )}
          </Row>
          {actionComponentError && <span className={'color-red-6'}>{actionComponentError}</span>}
        </Col>
      )}
    </Row>
  )
}

type ExpenseFormItemSelectProps = {
  itemDisplayName: string
  itemDisplayValue?: any
  inputName: string
  onChange?: (value: any) => void
  inputValue?: any
  disabled?: boolean
  customOption?: ReactNode
  customOptionClassname?: string
  actionIcon?: ActionIcon | null
  actionComponentError?: string
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void
}

const ExpenseFormItemSelect: React.FC<ExpenseFormItemSelectProps> = (props) => {
  const { t: translate } = useTranslation()

  return (
    <ExpenseFormItem
      itemName={props.itemDisplayName}
      itemValue={props.itemDisplayValue}
      actionComponentError={props.actionComponentError}
      actionComponent={
        <FormikSelectWithChildrenField
          name={props.inputName}
          value={props.inputValue}
          placeholder="Select"
          onKeyDown={props.onKeyDown}
          dropdownMatchSelectWidth={false}
          disabled={props.disabled}
          customOptionPlaceholder={
            props.customOption
              ? translate('doc-review-dashboard.expenses.review-expense-form.reviwer-override')
              : undefined
          }
          onChange={(value) => {
            if (props.onChange) {
              props.onChange(value)
            }
          }}
          customOption={props.customOption}
          customOptionClassname={props.customOptionClassname}
        >
          {reviewerActions.map((action) => (
            <Option key={action} value={action}>
              {reviewActionTitleMap.get(action)}
            </Option>
          ))}
        </FormikSelectWithChildrenField>
      }
      actionIcon={props.actionIcon}
    />
  )
}

const reviewerActions = [ReviewerAction.Verified, ReviewerAction.Missing]
const reviewActionTitleMap = new Map<ReviewerAction, string>([
  [ReviewerAction.Verified, 'Verified'],
  [ReviewerAction.Missing, 'Missing'],
  [ReviewerAction.Override, 'Override'],
])
